/**
 * The document and slides editing surface on web (plan 7.3): the save
 * controller lives here, the Tiptap editor is its own lazy chunk
 * (`DocumentEditorTiptap.web.tsx`), loaded only when Edit is pressed.
 */
import { Suspense, forwardRef, lazy, useEffect, useImperativeHandle, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SpinnerGlyph } from '../../common/SpinnerGlyph';
import type { EditorSaveState } from './save-state';
import { useDocumentEditorController } from './useDocumentEditorController';
import type { EditorHandle } from './PageEditor.web';

const DocumentEditorTiptap = lazy(() => import('./DocumentEditorTiptap.web'));

function watchBackground(flush: () => void): () => void {
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flush();
  };
  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', flush);
  return () => {
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', flush);
  };
}

export const DocumentEditor = forwardRef<
  EditorHandle,
  {
    artifactId: string;
    title: string;
    threadId: string | null;
    onSaveState: (state: EditorSaveState) => void;
    onAsked?: () => void;
  }
>(function DocumentEditor({ artifactId, title, threadId, onSaveState, onAsked }, ref) {
  const { t } = useTranslation('common');
  const controller = useDocumentEditorController({
    artifactId,
    translate: (key, params) => t(key, params ?? {}),
    onBackground: watchBackground,
  });
  useImperativeHandle(ref, () => ({ flush: controller.flush, retry: controller.retry, reload: controller.reload }), [
    controller.flush,
    controller.retry,
    controller.reload,
  ]);
  const onSaveStateRef = useRef(onSaveState);
  onSaveStateRef.current = onSaveState;
  useEffect(() => onSaveStateRef.current(controller.saveState), [controller.saveState]);

  const spinner = (
    <div className="tabbio-artifact-panel-message" role="status">
      <SpinnerGlyph color="var(--text-secondary)" size={22} />
      <span className="tabbio-artifact-panel-message-title">{t('chat.artifacts.edit.loading')}</span>
    </div>
  );

  if (controller.load.status === 'error') {
    return (
      <div className="tabbio-artifact-panel-message">
        <span className="tabbio-artifact-panel-message-title">{t('chat.artifacts.edit.loadFailed')}</span>
        <button type="button" className="tabbio-panel-primary" onClick={controller.reload}>
          {t('chat.artifacts.edit.retry')}
        </button>
      </div>
    );
  }
  if (controller.load.status !== 'ready' || !controller.document) return spinner;
  return (
    <Suspense fallback={spinner}>
      <DocumentEditorTiptap
        key={controller.load.generation}
        controller={controller}
        artifactId={artifactId}
        title={title}
        threadId={threadId}
        onAsked={onAsked}
      />
    </Suspense>
  );
});
